import React, { useState, useEffect } from 'react';
import { ManText, ManDatepicker } from "@craft-design-system/core-components-react";
import Select from 'react-select';
import UseDropdownTrucks from '../utils/dropdown_data_loading/useDropdownTrucks';
import useDynamicFiltering from '../utils/dropdown_data_loading/useDynamicFiltering';
import { useFilterContext } from './FilterContext';
import { useDynamicContext } from './DynamicContext';
import { useResetContext } from './ResetContext';
import { useDeselectContext } from './DeselectContext';
import Shimmer from "./Shimmer";
import FilterSidebar from './FilterSidebar';
import Yearly_mileage_filter from './Yearly_mileage_filter';

// Add a style tag for the loading animation
const loadingAnimationStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Truck_filters = ({ selectedTab, username }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedValues, setSelectedValues] = useState({
    username: { props: { username } },
    resetdata: 'Truck',
    button_clicked: false,
    startDate: null,
    endDate: null,
  });
  const [dynamicDropdownData, setDynamicDropdownData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFilterChanged, setLastFilterChanged] = useState(null);
  const [filterError, setFilterError] = useState(null);

  const { filterdata, updateFilterData } = useFilterContext();
  const { dynamicdata, updatedynamicdata } = useDynamicContext();
  const { resetdata, updateresetdata, clearGraphData } = useResetContext();
  const { deselectdata, updatedeselectdata } = useDeselectContext();

  const { dropdowndata, fetchData, isRefreshing } = UseDropdownTrucks();
  const { dynamicFiltering, dropdownInfo } = useDynamicFiltering();

  useEffect(() => {
    updateFilterData(selectedValues);
  }, []);

  // Removed automatic dynamic filtering - now triggered by button only

  const handleApplyDynamicFiltering = async () => {
    console.log("Apply Dynamic Filtering button clicked");
    
    // Check if there are any active filters
    const activeFiltersCount = Object.entries(selectedValues || {}).filter(([key, value]) => {
      return (
        !['username', 'resetdata', 'button_clicked', 'startDate', 'endDate', 'lastUpdated', 'lastChangedFilter'].includes(key) && 
        value && 
        (Array.isArray(value) ? value.length > 0 : true)
      );
    }).length;
    
    if (activeFiltersCount === 0) {
      console.log("No active filters to apply dynamic filtering");
      setFilterError("Please select at least one filter before applying dynamic filtering.");
      return;
    }
    
    await handleDynamicFiltering();
  };

  const handleDynamicFiltering = async (retryCount = 0) => {
    try {
      setIsLoading(true);
      setFilterError(null);
      
      console.log("Starting dynamic filtering with values:", selectedValues);
      
      // Get the filter that was last changed
      const changedFilter = selectedValues.lastChangedFilter;
      if (changedFilter) {
        setLastFilterChanged(changedFilter);
        console.log(`Last changed filter: ${changedFilter}`);
      }
      
      // Track which keys have actual selections
      const nonEmptyFilters = Object.entries(selectedValues).filter(([key, value]) => {
        const isSystemKey = ['username', 'resetdata', 'button_clicked', 'startDate', 'endDate', 'lastUpdated', 'lastChangedFilter'].includes(key);
        if (isSystemKey) return false;
        
        // Check if the value is non-empty
        return value && (Array.isArray(value) ? value.length > 0 : true);
      });
      
      console.log("Non-empty filters:", nonEmptyFilters.map(([key]) => key));
      
      // If no active filters, revert to original dropdown data
      if (nonEmptyFilters.length === 0) {
        console.log("No active filters, reverting to original dropdown data");
        setDynamicDropdownData(null);
        updatedynamicdata(null);
        setIsLoading(false);
        return;
      }
      
      // Create a clean version of selectedValues to send to the API
      const filteredValues = {};
      
      // Process each key-value pair with special handling for certain filters
      Object.entries(selectedValues || {}).forEach(([key, value]) => {
        // Skip internal tracking fields and null values
        if (['lastUpdated', 'lastChangedFilter'].includes(key) || value === null || value === undefined) {
          return;
        }
        
        // Log all filter values for debugging
        console.log(`Processing filter: ${key}`, value);
        
        // Special handling for segment filter (both uppercase and lowercase)
        if (key === "Segment" || key === "segment") {
          console.log(`Special handling for ${key} filter:`, value);
          
          // Only add values if they are valid
          if (Array.isArray(value) && value.length > 0) {
            // Use only one consistent key for segment to avoid conflicts
            // Check which column name is actually in the CSV file in the backend
            filteredValues["Segment"] = value; // This seems to be the column name in the CSV
            console.log("Added Segment filter values:", value);
            
            // For debugging, log what we're setting
            console.log("filteredValues now contains:", filteredValues);
          } else {
            console.log(`Skipping empty ${key} filter`);
          }
        } else {
          // Only add values if they are valid
          if (!(Array.isArray(value) && value.length === 0)) {
            filteredValues[key] = value;
          }
        }
      });
      
      // Log what's being sent to the backend
      console.log("Sending to dynamic filtering API:", JSON.stringify(filteredValues));
      
      // Call the dynamicFiltering function with the filtered selectedValues
      const dynamicResult = await dynamicFiltering({ selected_value: filteredValues });
      
      console.log("Raw dynamic filtering API response:", dynamicResult);
      
      if (!dynamicResult) {
        console.error("Dynamic filtering returned null or undefined result");
        setFilterError("No data returned from filtering API");
        
        // Retry logic
        if (retryCount < 2) {
          console.log(`Retrying dynamic filtering (attempt ${retryCount + 1})`);
          setIsLoading(false);
          return handleDynamicFiltering(retryCount + 1);
        }
        
        setIsLoading(false);
        return;
      }
      
      console.log("Dynamic filtering successful, processing results:", dynamicResult);
      
      // Perform validation and merging of results with original data
      let finalDropdownData = { ...dropdowndata }; // Start with original data
      let hasUpdates = false;
      
      // Check each key in the dynamic result
      Object.entries(dynamicResult).forEach(([key, values]) => {
        console.log(`Processing dynamic result for ${key}:`, values);
        
        // If the dynamic result for this key is empty or missing, keep the original
        if (!values || !Array.isArray(values) || values.length === 0) {
          console.warn(`Empty or invalid result for key ${key}, keeping original values`);
          // We don't change finalDropdownData[key] since we started with original data
        } else {
          // Use the dynamic results for this key
          console.log(`Updating ${key} with ${values.length} options`);
          finalDropdownData[key] = values;
          hasUpdates = true;
        }
      });
      
      // If no valid updates were made, log a warning but still try to use the result
      if (!hasUpdates) {
        console.warn("No valid updates in dynamic filtering results, but proceeding");
        // Still use the dynamic result even if it seems empty - might be intentional filtering
        finalDropdownData = { ...dynamicResult };
        hasUpdates = true;
      }
      
      // Ensure all selected values remain in the dropdown options
      Object.entries(selectedValues).forEach(([key, selectedVals]) => {
        if (!Array.isArray(selectedVals) || selectedVals.length === 0) return;
        if (!finalDropdownData[key]) return;
        
        // Make sure all selected values are in the dropdown options
        selectedVals.forEach(val => {
          if (!finalDropdownData[key].includes(val)) {
            console.log(`Adding selected value ${val} back to options for ${key}`);
            finalDropdownData[key].push(val);
          }
        });
      });
      
      console.log("Final dropdown data after processing:", finalDropdownData);
      console.log("About to update dynamicDropdownData state");
      
      // Update states
      setDynamicDropdownData(finalDropdownData);
      updatedynamicdata(finalDropdownData);
      
      console.log("Dynamic filtering completed successfully");
      console.log("dynamicDropdownData should now be:", finalDropdownData);
      
    } catch (error) {
      console.error("Error in dynamic filtering:", error);
      setFilterError(`Error: ${error.message}`);
      
      // Retry logic
      if (retryCount < 2) {
        console.log(`Retrying dynamic filtering (attempt ${retryCount + 1})`);
        setIsLoading(false);
        return handleDynamicFiltering(retryCount + 1);
      }
      
      // On error after retries, fall back to original dropdown data but preserve selected values
      setDynamicDropdownData(dropdowndata);
      updatedynamicdata(dropdowndata);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChange = (selectedOptions, columnName) => {
    setSelectedValues(prevValues => {
      // Handle null prevValues
      const safeValues = prevValues || {
        username: { props: { username } },
        resetdata: 'Truck',
        button_clicked: false,
        startDate: null,
        endDate: null,
      };
      
      const newValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
      
      console.log(`Filter changed: ${columnName}`, {
        previous: safeValues[columnName],
        new: newValues
      });
      
      // Special handling for Segment filter
      if (columnName === "Segment") {
        console.log("Segment filter selected:", newValues);
        // Check if the filter is being cleared
        if (newValues.length === 0) {
          console.log("Segment filter cleared");
        }
      }
      
      // Track what's changing - use safe optional chaining
      const isClearing = safeValues[columnName]?.length > 0 && newValues.length === 0;
      const isSelecting = (!safeValues[columnName] || safeValues[columnName]?.length === 0) && newValues.length > 0;
      const isChanging = safeValues[columnName]?.length > 0 && newValues.length > 0;
      
      if (isClearing) {
        console.log(`Selection for ${columnName} cleared - may need to reset dependent filters`);
      } else if (isSelecting) {
        console.log(`New selection for ${columnName} - other filters may become constrained`);
      } else if (isChanging) {
        console.log(`Selection for ${columnName} changed from [${safeValues[columnName]}] to [${newValues}]`);
      }
      
      // Handle filter name mapping for backend compatibility
      let adjustedColumnName = columnName;
      
      // If this is the "Segment" filter, map it correctly for the backend
      // The actual column name might be lowercase or have a different format
      if (columnName === "Segment") {
        adjustedColumnName = "segment"; // Try lowercase version
      }
      
      const updatedValues = {
        ...safeValues,
        [adjustedColumnName]: newValues,
        button_clicked: false,
        // Add a timestamp to ensure we capture each change even if the actual values don't change
        lastUpdated: new Date().getTime(),
        // Track which filter was last changed - can help with debugging
        lastChangedFilter: columnName
      };
      
      // For segment filter, use consistent casing as in the backend CSV file
      if (columnName === "Segment") {
        // Use exact case that matches the CSV file column names in the backend
        updatedValues.Segment = newValues;
        // Remove lowercase version to avoid confusion
        if ('segment' in updatedValues) {
          delete updatedValues.segment;
        }
        console.log(`Set Segment filter with exact case matching backend CSV:`, newValues);
      }
      
      updateFilterData(updatedValues);
      return updatedValues;
    });
  };

  const handleValuesChange = (minValue, maxValue) => {
    setSelectedValues(prevValues => {
      const updatedValues = {
        ...prevValues,
        'Total mileage [km]': [minValue, maxValue],
      };
      updateFilterData(updatedValues);
      return updatedValues;
    });
  };

  useEffect(() => {
    if (resetdata === 'Truck1') {
      fetchData();
      setSelectedValues(null);
      updateFilterData(null);
      // Clear dynamic dropdown data
      setDynamicDropdownData(null);
      updatedynamicdata(null);
    }
  }, [resetdata]);

  useEffect(() => {
    console.log('Truck_filters: dynamicdata changed', dynamicdata);
    if (dynamicdata) {
      setDynamicDropdownData(dynamicdata);
    }
  }, [dynamicdata]);

  const handleDateChange = (date, type) => {
    const newStartDate = type === 'start' ? date : startDate;
    const newEndDate = type === 'end' ? date : endDate;
    console.log('New Start Date:', newStartDate);
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    setSelectedValues(prevValues => {
      const updatedValues = {
        ...prevValues,
        startDate: newStartDate?.toISOString() || null,
        endDate: newEndDate?.toISOString() || null,
      };
      updateFilterData(updatedValues);
      return updatedValues;
    });
  };

  const customStyles = {
    dropdownIndicator: (provided) => ({ ...provided, color: 'red' }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'grey' : state.isFocused ? '#EAEDF3' : null,
      color: state.isSelected ? 'white' : 'black',
    }),
    control: (provided) => ({
      ...provided,
      borderRadius: '1px',
      borderWidth: '1px',
      borderColor: '#5B6F85',
      cursor: 'pointer',
    }),
    multiValue: (styles) => ({ ...styles }),
    valueContainer: (styles) => ({
      ...styles,
      display: 'flex',
      flexWrap: 'nowrap',
      overflowX: 'auto',
    }),
  };
  if (!dropdowndata || isRefreshing) return <Shimmer />;

  // Use dynamic dropdown data if available, otherwise use the original dropdown data
  const displayDropdownData = dynamicDropdownData || dropdowndata;
  
  // Handle reset of filters
  const handleResetFilters = () => {
    console.log("Resetting all filters");
    
    // Initial state with default values
    const initialState = {
      username: { props: { username } },
      resetdata: 'Truck',
      button_clicked: false,
      startDate: null,
      endDate: null,
    };
    
    // Reset all state
    setSelectedValues(initialState);
    setStartDate(null);
    setEndDate(null);
    setDynamicDropdownData(null);
    updatedynamicdata(null);
    setLastFilterChanged(null);
    setFilterError(null);
    
    // Update filter context
    updateFilterData(initialState);
    
    // Fetch original dropdown data
    fetchData();
    
    console.log("All filters have been reset");
  };

  // Check if any filter has zero options
  const hasEmptyFilters = dynamicDropdownData && 
    Object.values(dynamicDropdownData).some(arr => arr && arr.length === 0);

  return (
    <>
      <style>{loadingAnimationStyle}</style>
      {/* Loading Indicator */}
      {isLoading && (
        <div style={{ 
          backgroundColor: '#e6f7ff', 
          padding: '10px', 
          marginBottom: '10px',
          border: '1px solid #91d5ff',
          borderRadius: '4px',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ width: '20px', height: '20px', borderRadius: '50%', 
                      border: '2px solid #1890ff', borderTopColor: 'transparent', 
                      animation: 'spin 1s linear infinite', marginRight: '10px' }} />
          <ManText variant={"body-s"} as={"span"} style={{ color: '#1890ff' }}>
            Updating filters...
          </ManText>
        </div>
      )}
      
      {/* Error Message */}
      {filterError && (
        <div style={{ 
          backgroundColor: '#fff1f0', 
          padding: '10px', 
          marginBottom: '10px',
          border: '1px solid #ffa39e',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          <ManText variant={"body-s"} as={"span"} style={{ color: '#cf1322' }}>
            {filterError}
          </ManText>
          <button 
            onClick={handleResetFilters}
            style={{ 
              marginLeft: '10px', 
              backgroundColor: '#cf1322', 
              color: 'white', 
              border: 'none', 
              padding: '4px 8px', 
              borderRadius: '2px', 
              cursor: 'pointer'
            }}
          >
            Reset Filters
          </button>
        </div>
      )}
      
      {/* Empty Filter Options Warning */}
      {hasEmptyFilters && !isLoading && !filterError && (
        <div style={{ 
          backgroundColor: '#fff1f0', 
          padding: '10px', 
          marginBottom: '10px',
          border: '1px solid #ffa39e',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          <ManText variant={"body-s"} as={"span"} style={{ color: '#cf1322' }}>
            Some filters have no options due to your selections.
            {lastFilterChanged && ` Last changed filter: ${lastFilterChanged}`}
          </ManText>
          <button 
            onClick={handleResetFilters}
            style={{ 
              marginLeft: '10px', 
              backgroundColor: '#cf1322', 
              color: 'white', 
              border: 'none', 
              padding: '4px 8px', 
              borderRadius: '2px', 
              cursor: 'pointer'
            }}
          >
            Reset Filters
          </button>
        </div>
      )}
      
      {/* Filter Sidebar */}
      <FilterSidebar
        dropdownData={displayDropdownData}
        selectedValues={selectedValues}
        handleSelectChange={handleSelectChange}
        customStyles={customStyles}
        onvaluesChange={handleValuesChange}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        isDynamicFiltering={!!dynamicDropdownData}
        onResetFilters={handleResetFilters}
        onApplyDynamicFiltering={handleApplyDynamicFiltering}
        isLoading={isLoading}
      />
    </>
  );
};

export default Truck_filters;

